import { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { useChat } from '@/hooks/useChat';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface ChatWidgetProps {
  roomId?: string;
  otherUserId?: string;
  className?: string;
}

export const ChatWidget = ({ roomId: initialRoomId, otherUserId, className }: ChatWidgetProps) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [roomId, setRoomId] = useState(initialRoomId);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  const {
    messages,
    typingUsers,
    loading,
    sendMessage,
    createDirectChat,
    markAsRead,
    setTyping,
    fetchMessages,
  } = useChat(roomId);

  // Create direct chat if otherUserId is provided
  useEffect(() => {
    if (otherUserId && !roomId && user) {
      createDirectChat(otherUserId).then(newRoomId => {
        if (newRoomId) {
          setRoomId(newRoomId);
        }
      });
    }
  }, [otherUserId, roomId, user, createDirectChat]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark as read when opening chat
  useEffect(() => {
    if (isOpen && roomId) {
      markAsRead(roomId);
    }
  }, [isOpen, roomId, markAsRead]);

  const handleSend = async () => {
    if (!message.trim() || !roomId || isSending) return;

    setIsSending(true);
    try {
      await sendMessage(roomId, message);
      setMessage('');
      setTyping(roomId, false);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleTyping = (value: string) => {
    setMessage(value);

    if (!roomId) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set typing indicator
    if (value.trim()) {
      setTyping(roomId, true);

      // Clear typing after 3 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        setTyping(roomId, false);
      }, 3000);
    } else {
      setTyping(roomId, false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className={cn('fixed bottom-4 right-4 z-50', className)}>
      {isOpen ? (
        <Card className="w-96 h-[500px] flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Chat</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No messages yet. Start the conversation!
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => {
                  const isOwn = msg.sender_id === user?.id;
                  return (
                    <div
                      key={msg.id}
                      className={cn(
                        'flex gap-2',
                        isOwn ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={msg.sender?.avatar_url} />
                        <AvatarFallback>
                          {msg.sender?.full_name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div
                        className={cn(
                          'flex flex-col max-w-[70%]',
                          isOwn ? 'items-end' : 'items-start'
                        )}
                      >
                        <div
                          className={cn(
                            'rounded-lg px-3 py-2',
                            isOwn
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          )}
                        >
                          <p className="text-sm">{msg.content}</p>
                        </div>
                        <span className="text-xs text-muted-foreground mt-1">
                          {formatDistanceToNow(new Date(msg.created_at), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {typingUsers.length > 0 && (
                  <div className="flex gap-2 items-center text-sm text-muted-foreground">
                    <div className="flex gap-1">
                      <span className="animate-bounce">.</span>
                      <span className="animate-bounce delay-100">.</span>
                      <span className="animate-bounce delay-200">.</span>
                    </div>
                    <span>Someone is typing...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex gap-2"
            >
              <Input
                value={message}
                onChange={(e) => handleTyping(e.target.value)}
                placeholder="Type a message..."
                disabled={isSending || !roomId}
              />
              <Button
                type="submit"
                size="icon"
                disabled={!message.trim() || isSending || !roomId}
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </Card>
      ) : (
        <Button
          size="lg"
          className="rounded-full h-14 w-14 shadow-lg"
          onClick={() => setIsOpen(true)}
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
};
